import { z } from 'zod'

/** Imagens aceitas no upload. HEIC fica fora: o navegador nao renderiza. */
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const

/** Anexos de documento. O RF01.1 pede PDF; nao abrimos para Office. */
export const DOCUMENT_MIME_TYPES = ['application/pdf'] as const

export const UPLOADABLE_MIME_TYPES = [...IMAGE_MIME_TYPES, ...DOCUMENT_MIME_TYPES] as const

export const MAX_IMAGE_BYTES = 12 * 1024 * 1024
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024

export const mediaKindSchema = z.enum(['IMAGE', 'DOCUMENT'])
export type MediaKind = z.infer<typeof mediaKindSchema>

export function kindForMimeType(mimeType: string): MediaKind | null {
  if ((IMAGE_MIME_TYPES as readonly string[]).includes(mimeType)) return 'IMAGE'
  if ((DOCUMENT_MIME_TYPES as readonly string[]).includes(mimeType)) return 'DOCUMENT'
  return null
}

export function maxBytesForMimeType(mimeType: string): number {
  return kindForMimeType(mimeType) === 'DOCUMENT' ? MAX_DOCUMENT_BYTES : MAX_IMAGE_BYTES
}

/**
 * Pedido de URL pre-assinada. O arquivo vai do navegador direto para o R2 -
 * a API nunca recebe os bytes, entao o upload de um PDF de 20 MB nao ocupa o
 * processo Node que atende o portal.
 */
export const presignUploadSchema = z.object({
  fileName: z.string().min(1, 'Nome do arquivo obrigatorio').max(255),
  mimeType: z.string().refine((v) => kindForMimeType(v) !== null, {
    error: 'Formato nao aceito. Envie imagem (JPG, PNG, WebP, AVIF) ou PDF.',
  }),
  byteSize: z.coerce.number().int().positive(),
})

export type PresignUploadInput = z.infer<typeof presignUploadSchema>

export const presignUploadResponseSchema = z.object({
  mediaId: z.string(),
  uploadUrl: z.string(),
  key: z.string(),
  publicUrl: z.string(),
  /** Segundos de validade da URL assinada. */
  expiresIn: z.number(),
})

/** Confirmacao pos-upload: e aqui que a midia deixa de ser PENDING. */
export const confirmUploadSchema = z.object({
  alt: z.string().max(300).trim().optional(),
  caption: z.string().max(300).trim().optional(),
})

export const mediaSchema = z.object({
  id: z.string(),
  kind: mediaKindSchema,
  key: z.string(),
  url: z.string(),
  mimeType: z.string(),
  byteSize: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  alt: z.string().nullable(),
  caption: z.string().nullable(),
  originalName: z.string(),
  createdAt: z.string(),
})

export type Media = z.infer<typeof mediaSchema>

// --- Galeria de fotos ------------------------------------------------------

/**
 * Limite de fotos por galeria.
 *
 * Nao e numero redondo por estetica: uma festa paroquial rende facilmente 200
 * fotos, e publicar todas transformaria a pagina em alguns megabytes - o que
 * pesa justamente para quem abre no 4G da praca. Sessenta cobre a selecao que
 * alguem de fato escolhe a dedo.
 */
export const MAX_FOTOS_NA_GALERIA = 60

export const galleryImageInputSchema = z.object({
  mediaId: z.string().min(1),
  /**
   * Legenda visivel. Opcional, e diferente do `alt`: a legenda conta o que a
   * foto mostra para quem a ve ("Procissao saindo da matriz"), o `alt` descreve
   * a imagem para quem nao a ve e vive no cadastro da midia.
   */
  caption: z.string().max(300).trim().optional(),
})

export type GalleryImageInput = z.infer<typeof galleryImageInputSchema>

/** A ordem do array e a ordem de exibicao. */
export const gallerySchema = z
  .array(galleryImageInputSchema)
  .max(MAX_FOTOS_NA_GALERIA, `No maximo ${MAX_FOTOS_NA_GALERIA} fotos na galeria`)
  .default([])
